import { useCallback, useEffect, useRef, useState } from 'react';

const OSS_BASE_URL = 'https://xiaoshuoassets.oss-ap-southeast-1.aliyuncs.com/audios';
const CLIP_END_TOLERANCE = 0.12;

const parseTimestamp = (value) => {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * 共享音频片段播放 Hook
 * 用于播放收藏句子的中英文音频片段
 */
const useAudioClipPlayer = () => {
  const [playingClip, setPlayingClip] = useState({ en: null, cn: null });

  const audioRefs = useRef({
    en: typeof Audio !== 'undefined' ? new Audio() : null,
    cn: typeof Audio !== 'undefined' ? new Audio() : null,
  });
  const audioChapterRef = useRef({ en: null, cn: null });
  const audioTimeUpdateRefs = useRef({ en: null, cn: null });
  const playbackTokenRef = useRef({ en: 0, cn: 0 });

  useEffect(() => {
    const { en, cn } = audioRefs.current;
    [en, cn].forEach((audio) => {
      if (audio) audio.preload = 'auto';
    });
    return () => {
      ['en', 'cn'].forEach((lang) => {
        const audio = audioRefs.current[lang];
        const handler = audioTimeUpdateRefs.current[lang];
        if (audio && handler) {
          audio.removeEventListener('timeupdate', handler);
          audioTimeUpdateRefs.current[lang] = null;
        }
        if (audio) {
          try {
            audio.pause();
          } catch (_) { /* ignore */ }
          audio.src = '';
        }
        audioChapterRef.current[lang] = null;
      });
    };
  }, []);

  const ensureAudioMetadata = useCallback((audio) => {
    if (!audio) return Promise.resolve();
    if (audio.readyState >= 1 && !Number.isNaN(audio.duration)) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const handleLoaded = () => {
        audio.removeEventListener('loadedmetadata', handleLoaded);
        audio.removeEventListener('error', handleError);
        resolve();
      };
      const handleError = () => {
        audio.removeEventListener('loadedmetadata', handleLoaded);
        audio.removeEventListener('error', handleError);
        resolve();
      };
      audio.addEventListener('loadedmetadata', handleLoaded, { once: true });
      audio.addEventListener('error', handleError, { once: true });
      try {
        audio.load();
      } catch (err) {
        audio.removeEventListener('loadedmetadata', handleLoaded);
        audio.removeEventListener('error', handleError);
        resolve();
      }
    });
  }, []);

  const seekAudioTime = useCallback((audio, time) => {
    if (!audio) return Promise.resolve();
    const targetTime = Number.isFinite(time) ? Math.max(time, 0) : 0;
    if (Math.abs(audio.currentTime - targetTime) < 0.05) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const handleSeeked = () => {
        audio.removeEventListener('seeked', handleSeeked);
        resolve();
      };
      audio.addEventListener('seeked', handleSeeked, { once: true });
      try {
        audio.currentTime = targetTime;
      } catch (err) {
        audio.removeEventListener('seeked', handleSeeked);
        resolve();
      }
    });
  }, []);

  const stopClip = useCallback((lang, resetToken = false) => {
    const audio = audioRefs.current[lang];
    if (!audio) return;
    const handler = audioTimeUpdateRefs.current[lang];
    if (handler) {
      audio.removeEventListener('timeupdate', handler);
      audioTimeUpdateRefs.current[lang] = null;
    }
    if (resetToken) playbackTokenRef.current[lang] += 1;
    try {
      audio.pause();
    } catch (_) { /* ignore */ }
    setPlayingClip((prev) => (prev[lang] === null ? prev : { ...prev, [lang]: null }));
  }, []);

  const playSentenceClip = useCallback(async (sentence, lang) => {
    if (!sentence) return;
    const audio = audioRefs.current[lang];
    if (!audio) return;

    const chapterId = sentence.chapter_id;
    if (chapterId === null || chapterId === undefined) return;

    const startKey = lang === 'en' ? 'en_start_timestamp' : 'cn_start_timestamp';
    const endKey = lang === 'en' ? 'en_end_timestamp' : 'cn_end_timestamp';
    const start = parseTimestamp(sentence[startKey]);
    const end = parseTimestamp(sentence[endKey]);
    if (start === null || end === null || end - start <= 0.05) return;

    playbackTokenRef.current[lang] += 1;
    const token = playbackTokenRef.current[lang];

    stopClip('en');
    stopClip('cn');

    const expectedSrc = `${OSS_BASE_URL}/${chapterId}/${lang}.mp3`;
    if (audioChapterRef.current[lang] !== chapterId || audio.src !== expectedSrc) {
      audioChapterRef.current[lang] = chapterId;
      audio.src = expectedSrc;
    }

    await ensureAudioMetadata(audio);
    if (playbackTokenRef.current[lang] !== token) return;

    await seekAudioTime(audio, start);
    if (playbackTokenRef.current[lang] !== token) return;

    const handleTimeUpdate = () => {
      if (audio.currentTime >= end - CLIP_END_TOLERANCE) {
        stopClip(lang, true);
      }
    };

    audioTimeUpdateRefs.current[lang] = handleTimeUpdate;
    audio.addEventListener('timeupdate', handleTimeUpdate);

    try {
      await audio.play();
      if (playbackTokenRef.current[lang] !== token) {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        if (audioTimeUpdateRefs.current[lang] === handleTimeUpdate) {
          audioTimeUpdateRefs.current[lang] = null;
        }
        return;
      }
      setPlayingClip((prev) => ({ ...prev, [lang]: sentence.id }));
    } catch (err) {
      console.error('播放音频失败:', err);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      if (audioTimeUpdateRefs.current[lang] === handleTimeUpdate) {
        audioTimeUpdateRefs.current[lang] = null;
      }
      stopClip(lang, true);
    }
  }, [ensureAudioMetadata, seekAudioTime, stopClip]);

  return {
    playingClip,
    playSentenceClip,
    stopClip,
  };
};

export default useAudioClipPlayer;

